import type { Ref } from 'react'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { BankPicker } from '@/components/BankPicker'
import { BVNInput, type BVNInputHandle } from './BVNInput'
import type { BlockType } from '../types'

interface AccountValue {
  bankCode: string | null
  nuban: string
}

interface Props {
  type: BlockType
  /** Generic identifier for non-account types. */
  identifier: string
  setIdentifier: (next: string) => void
  /** Account-specific fields. */
  account: AccountValue
  setAccount: (next: AccountValue) => void
  /** Imperative ref to the BVN input — parent reads raw + hashes on submit. */
  bvnRef: Ref<BVNInputHandle>
  bvnPepperReady: boolean
  bvnValid: boolean
  setBvnValid: (next: boolean) => void
  error?: string
}

const ACCOUNT_NUBAN_RE = /^\d{0,10}$/
const IP_RE =
  /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$/

/**
 * AF-128 — Block target field.
 *
 * Renders the right input(s) for the selected `type`:
 *   - account  → BankPicker + 10-digit NUBAN
 *   - customer → "C-XXXX" customer id input
 *   - card     → card token (server resolves; never raw PAN)
 *   - bvn      → BVNInput (HMAC-hashed at submit)
 *   - device   → device fingerprint id
 *   - ip       → IP address (validated)
 *   - terminal → POS terminal id
 */
export function TargetField({
  type,
  identifier,
  setIdentifier,
  account,
  setAccount,
  bvnRef,
  bvnPepperReady,
  bvnValid,
  setBvnValid,
  error,
}: Props) {
  if (type === 'account') {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="block-target-bank">Bank</Label>
          <BankPicker
            id="block-target-bank"
            value={account.bankCode}
            onChange={(code) => setAccount({ ...account, bankCode: code })}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="block-target-nuban">NUBAN</Label>
          <Input
            id="block-target-nuban"
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            value={account.nuban}
            onChange={(e) => {
              const v = e.target.value.replace(/\D+/g, '')
              if (ACCOUNT_NUBAN_RE.test(v)) setAccount({ ...account, nuban: v })
            }}
            placeholder="10-digit NUBAN"
            maxLength={10}
            className="font-mono tabular-nums"
          />
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    )
  }

  if (type === 'bvn') {
    return (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="block-target-bvn">BVN</Label>
        <BVNInput
          id="block-target-bvn"
          ref={bvnRef}
          pepperReady={bvnPepperReady}
          onValidityChange={setBvnValid}
        />
        {error && !bvnValid && (
          <p className="text-xs text-destructive">{error}</p>
        )}
      </div>
    )
  }

  const helper = HELPERS[type]
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="block-target-id">{helper.label}</Label>
      <Input
        id="block-target-id"
        autoComplete="off"
        spellCheck={false}
        value={identifier}
        onChange={(e) => setIdentifier(e.target.value)}
        placeholder={helper.placeholder}
        invalid={
          type === 'ip' && identifier.length > 0 && !IP_RE.test(identifier)
        }
        className={helper.mono ? 'font-mono tabular-nums' : undefined}
      />
      <p className="text-xs text-muted-fg">{helper.hint}</p>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

const HELPERS: Record<
  Exclude<BlockType, 'account' | 'bvn'>,
  { label: string; placeholder: string; hint: string; mono?: boolean }
> = {
  customer: {
    label: 'Customer ID',
    placeholder: 'cust-XXXX or C-XXXX',
    hint: 'Internal customer ID. The Customer 360 page is the easiest way to find this.',
  },
  card: {
    label: 'Card token',
    placeholder: 'card-XXXX',
    hint: "Tokenised card identifier — never paste a raw PAN.",
    mono: true,
  },
  device: {
    label: 'Device ID',
    placeholder: 'dev-XXXX',
    hint: 'Device fingerprint id from the mobile app or web session.',
    mono: true,
  },
  ip: {
    label: 'IP address',
    placeholder: '203.0.113.42',
    hint: 'Single IP. Range blocks should be filed via network ops.',
    mono: true,
  },
  terminal: {
    label: 'Terminal ID',
    placeholder: '2087C5A1',
    hint: 'POS terminal id. Visible on the POS case block when investigating.',
    mono: true,
  },
}

export { IP_RE }
